/**
 * The register on a map: a filter column on the left, the country on the right.
 *
 * Filter state lives in the URL rather than in `useState`, which is the one
 * decision here worth arguing about. It costs a little ceremony and buys three
 * things the list view does not have: switching to the list tab and back does
 * not throw the filters away, a territory somebody set up can be sent to a
 * colleague as a link, and the browser's own back button undoes a filter. For a
 * screen whose whole job is "look at THIS slice of the country", being able to
 * name the slice is most of the value.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Maximize2, MapPinOff, Minimize2, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { cn, type ComboboxOption } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { EMTAK_ACTIVITY_OPTIONS } from "../../../../lib/emtak";
import {
  ANY,
  EMPTY_AUDIENCE_DRAFT,
  draftFromAudience,
  EMPTY_AUDIENCE_FILTER,
  audienceToParams,
  registeredRange,
  toIsoDate,
  type AudienceDraft,
  type AudienceFilter,
} from "../../../../lib/audienceFilter";
import { getMapCompanies, type MapPoint, type MapResult } from "../../../../lib/companyMap";
import { EmptyState, fmtNum } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { GeocodeBar } from "./GeocodeBar";
import { useMapFullscreen } from "./useMapFullscreen";
import { MapPickCard } from "./MapPickCard";
import { MapFilterRail } from "./MapFilterRail";
import { drawCells, drawPoints, type DrawnLayer } from "./markers";
import { useCompanyMap } from "./useCompanyMap";

/** How long typing sits still before it becomes a filter. Matches the list. */
const DEBOUNCE_MS = 350;

/** The keys the rail can put in the URL. Anything else there is not ours. */
const URL_KEYS = [
  "q", "status", "county", "legalForm", "emtak", "hasVat", "hasEmail", "hasPhone",
  "hasWebsite", "capitalMin", "capitalMax", "revenueMin", "revenueMax",
  "employeesMin", "employeesMax", "webNeedMin", "webNeedMax", "profitable",
  "hasDebt", "debtMin", "hasLicence", "licenceSince", "wonProcurement", "procurementSince",
  "registeredFrom", "registeredTo",
] as const;

/**
 * URL -> filter. Absent means "do not filter", which for a select is `ANY` and
 * for a switch is false - the same asymmetry `audienceToParams` has on the way
 * out, kept here so a round trip through the URL changes nothing.
 */
function filterFromParams(sp: URLSearchParams): AudienceFilter {
  const get = (k: string) => sp.get(k) ?? "";
  const sel = (k: string) => sp.get(k) || ANY;
  return {
    q: get("q"),
    status: sel("status"),
    county: sel("county"),
    legalForm: sel("legalForm"),
    emtak: get("emtak"),
    hasVat: sel("hasVat"),
    hasEmail: sp.get("hasEmail") === "true",
    hasPhone: sp.get("hasPhone") === "true",
    hasWebsite: sel("hasWebsite"),
    capitalMin: get("capitalMin"),
    capitalMax: get("capitalMax"),
    revenueMin: get("revenueMin"),
    revenueMax: get("revenueMax"),
    employeesMin: get("employeesMin"),
    employeesMax: get("employeesMax"),
    webNeedMin: get("webNeedMin"),
    webNeedMax: get("webNeedMax"),
    profitable: sel("profitable"),
    hasDebt: sel("hasDebt"),
    debtMin: get("debtMin"),
    hasLicence: sel("hasLicence"),
    licenceSince: sp.get("licenceSince"),
    wonProcurement: sel("wonProcurement"),
    procurementSince: sp.get("procurementSince"),
    registeredFrom: sp.get("registeredFrom"),
    registeredTo: sp.get("registeredTo"),
  };
}

function countActive(f: AudienceFilter): number {
  const p = audienceToParams(f);
  return Object.values(p).filter((v) => v !== undefined && v !== "").length;
}

export function MapView({ user }: { user: StaffUser }) {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  const host = useRef<HTMLDivElement | null>(null);
  const { phase, map, view } = useCompanyMap(host);

  const filters = useMemo(() => filterFromParams(sp), [sp]);
  // `draftFromAudience` rather than the same nine assignments written out again:
  // a URL carrying a bound this list forgot would come up blank in the rail and
  // then be cleared by the first debounce tick.
  const [draft, setDraft] = useState<AudienceDraft>(() => draftFromAudience(filters));

  const [result, setResult] = useState<MapResult | null>(null);
  const [loading, setLoading] = useState(false);
  // A whole address, not one company: a pin stands for every company registered
  // at that coordinate (see `groupByPosition`).
  const [picked, setPicked] = useState<MapPoint[] | null>(null);
  const [redrawKey, setRedrawKey] = useState(0);

  /** Writes a filter change into the URL, dropping the ones that mean nothing. */
  const commit = useCallback(
    (next: AudienceFilter) => {
      const params = audienceToParams(next);
      const out = new URLSearchParams();
      for (const k of URL_KEYS) {
        const v = (params as Record<string, unknown>)[k];
        if (v !== undefined && v !== "" && v !== null) out.set(k, String(v));
      }
      // `replace` so a filter is not twenty entries in the back stack; the URL
      // is a description of the current view, not a trail of keystrokes.
      setSp(out, { replace: true });
    },
    [setSp],
  );

  const patch = useCallback(
    (change: Partial<AudienceFilter>) => commit({ ...filters, ...change }),
    [commit, filters],
  );

  // The typing buffer becomes a filter once the operator stops. Every one of
  // these fields is a text input; the selects commit immediately.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      // Derived from the draft's own keys rather than listed by hand. The list
      // was nine hand-written comparisons, which is nine chances for the next
      // debounced field to be added to the rail and left out here - and the
      // failure would be invisible: the field would work, it would just also
      // re-commit the filter every 350 ms forever.
      const settled = (Object.keys(draft) as (keyof AudienceDraft)[]).every((k) => draft[k] === filters[k]);
      if (settled) return;
      commit({ ...filters, ...draft });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, filters, commit]);

  const params = useMemo(() => audienceToParams(filters), [filters]);

  // The viewport query. Re-runs on every settled pan, every filter change, and
  // whenever a geocoding pass finishes and puts new companies on the map.
  useEffect(() => {
    if (!view) return;
    const ac = new AbortController();
    setLoading(true);
    getMapCompanies(view.bounds, view.zoom, params, ac.signal)
      .then((r) => setResult(r))
      .catch(() => {
        // An aborted request is the normal case while dragging, and the answer
        // that replaces it is already on its way. Nothing to report.
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [view, params, redrawKey]);

  // Markers are drawn imperatively and replaced wholesale - see `markers.ts`
  // for why React does not own them.
  useEffect(() => {
    if (!map || !result) return;
    const layer: DrawnLayer =
      result.mode === "cells"
        ? drawCells(map, result.cells)
        : drawPoints(map, result.points, setPicked);
    return () => layer.clear();
  }, [map, result]);

  const activityOptions = useMemo<ComboboxOption[]>(
    () => EMTAK_ACTIVITY_OPTIONS.map((o) => ({ value: o.value, label: o.label, hint: o.hint })),
    [],
  );

  const regRange = useMemo(() => registeredRange(filters), [filters]);

  const onPlaced = useCallback(() => setRedrawKey((k) => k + 1), []);

  const activeCount = countActive(filters);
  const canManage = userCanPlatform(user, "manage:ariregister");

  // Putting the map back on the centre it was showing after the frame changes
  // size. Google keeps the top-left corner across a resize, not the middle, so
  // without this a fullscreen toggle slides the country sideways.
  const shell = useRef<HTMLDivElement | null>(null);
  const recentre = useCallback(() => {
    if (!map) return;
    const centre = map.getCenter();
    if (centre) map.setCenter(centre);
  }, [map]);
  const { full, toggle: toggleFull } = useMapFullscreen(shell, recentre);

  // ⚠ The whole view sits inside a PopHost, and it is not decoration. Every
  // dropdown in this app reads its open state off that context and a `Pop`
  // without one is inert - it renders, it takes the click, and nothing happens.
  // The rail's county, activity and status menus were dead for exactly that
  // reason until 2026-08-16, and the map was never drawn locally (no Maps key)
  // so nobody had clicked them. Each page mounts its own host rather than the
  // layout owning one, so that closing is scoped to the page.
  return (
    <PopHost>
    {/* ⚠ ONE tree for both modes. Fullscreen changes classes only, never the
        structure: the map host below carries Google's whole render tree, and
        React recreating that div would leave the map drawing into a node that
        is no longer in the document. Everything the two modes differ by lives
        in `.crm-mapfull` in index.css. */}
    <div ref={shell} className={cn("flex h-full min-h-0 flex-col", full && "crm-mapfull")}>
      {full ? null : <GeocodeBar canManage={canManage} onPlaced={onPlaced} />}

      <div
        className={cn(
          "crm-card grid min-h-0 flex-1 grid-cols-1 overflow-hidden",
          full ? "crm-mapfull-grid" : "lg:grid-cols-[19rem_1fr]",
        )}
      >
        <div
          className={cn(
            // Glass in both modes: the screen changes geometry when it goes
            // fullscreen, not material.
            "crm-maprail is-glass",
            full ? "is-full" : "border-b border-[var(--crm-hair)] lg:border-b-0 lg:border-r",
          )}
        >
          <MapFilterRail
            filters={filters}
            draft={draft}
            setDraft={setDraft}
            patch={patch}
            activityOptions={activityOptions}
            regRange={regRange}
            onRegRange={(r) =>
              patch({
                registeredFrom: r ? toIsoDate(r.from, false) : null,
                registeredTo: r ? toIsoDate(r.to, true) : null,
              })
            }
            activeCount={activeCount}
            onClear={() => {
              setDraft(EMPTY_AUDIENCE_DRAFT);
              commit(EMPTY_AUDIENCE_FILTER);
            }}
          />
        </div>

        <div className="relative min-h-[26rem]">
          <div ref={host} className="absolute inset-0" />

          {phase === "no-key" ? (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <EmptyState
                icon={MapPinOff}
                title={t("ariregister.map.noKeyTitle")}
                body={t("ariregister.map.noKeyBody")}
                action={
                  <Link to="/settings/integrations/maps" className="crm-btn crm-btn-sm">
                    {t("ariregister.map.noKeyLink")}
                  </Link>
                }
              />
            </div>
          ) : null}

          {phase === "failed" ? (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <EmptyState
                icon={TriangleAlert}
                title={t("ariregister.map.failedTitle")}
                body={t("ariregister.map.failedBody")}
              />
            </div>
          ) : null}

          {/* The honest count: how many companies match in this viewport, which
              is not the same as how many pins were drawn. When the server had
              to cluster, the strip says so rather than letting the operator
              read the bubbles as the whole answer. */}
          <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
            {phase === "ready" && result ? (
              <div className="crm-card flex items-center gap-2 px-3 py-2 text-[12.5px] shadow">
                {loading ? <Loader2 className="size-3.5 shrink-0 animate-spin opacity-70" /> : null}
                <span className="crm-tnum font-[650]">{fmtNum(result.total)}</span>
                <span className="crm-mut">
                  {result.mode === "cells"
                    ? t("ariregister.map.inViewClustered")
                    : t("ariregister.map.inView")}
                </span>
              </div>
            ) : null}
            {/* Offered whatever the map's phase is: an operator looking at the
                "no key" panel is the one most likely to want more room for the
                thing they are about to fix. */}
            <button
              type="button"
              onClick={toggleFull}
              className="crm-card crm-cb crm-cb-sm shadow"
              title={t(full ? "ariregister.map.exitFullscreen" : "ariregister.map.fullscreen")}
              aria-label={t(full ? "ariregister.map.exitFullscreen" : "ariregister.map.fullscreen")}
              aria-pressed={full}
            >
              {full ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
            </button>
          </div>

          {picked ? <MapPickCard group={picked} onClose={() => setPicked(null)} /> : null}
        </div>
      </div>
    </div>
    </PopHost>
  );
}
